import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import { computeRowProps, Rowprop } from "../yield-sheet/rowProp";
import SectionTitle from "../elements/sectionTitle";
import { ThemeProvider } from "@mui/material/styles";
import { baseTheme } from "mui-theme";
import { tableTheme } from "mui-theme/table";
import { parseISO } from "date-fns";

import {
  KeyboardArrowDown,
  KeyboardArrowRight,
  Search,
  CancelRounded,
} from "@mui/icons-material";
import {
  InputAdornment,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Checkbox,
  FormControlLabel,
  Box,
  IconButton,
  Typography,
  TextField,
} from "@mui/material";

interface State {
  selections: {
    [roomType: string]: {
      selected: boolean;
      ratePlans: {
        [ratePlan: string]: boolean;
      };
    };
  };
  allRTSelected: boolean;
  allRPSelected: boolean;
  rowProps?: Rowprop;
  collapsed: string[];
  filtered: string[];
  filterValue: string;
}

interface StateProps {
  activeHotel: boolean;
}

interface Props extends StateProps {
  hotelId: number;
  rate: number;
  manualOnly: boolean;
  selectable?: boolean;
  clearTrigger?: string;
  onSelection?: (selections: State["selections"]) => void;
  periods?: { start: string; end: string; idx: number }[];
  weekDays?: boolean[];
}

class BulkUpdatePreview extends Component<Props, State> {
  _isMounted = false;
  readonly state: State = {
    selections: {},
    allRTSelected: false,
    allRPSelected: false,
    collapsed: [],
    filtered: [],
    filterValue: "",
  };

  constructor(props: Props) {
    super(props);

    const selections: State["selections"] = {};
    const collapsed: string[] = [];

    this.toggleCollapse = this.toggleCollapse.bind(this);
    this.applyFilter = this.applyFilter.bind(this);
    this.collapseAll = this.collapseAll.bind(this);
    this.toggleAllRP = this.toggleAllRP.bind(this);
    this.clearFilter = this.clearFilter.bind(this);

    this.state = {
      selections,
      rowProps: undefined,
      allRTSelected: false,
      allRPSelected: false,
      collapsed,
      filtered: [],
      filterValue: "",
    };
  }

  async componentDidMount() {
    this._isMounted = true;
    const rowProps = await computeRowProps(this.props.activeHotel);

    if (this._isMounted) {
      const selections = { ...this.state.selections };
      const collapsed = [...this.state.collapsed];

      rowProps.rtConf.forEach((roomConfig: any) => {
        collapsed.push(roomConfig.id);
        selections[roomConfig.id] = {
          selected: false,
          ratePlans: {},
        };
      });

      this.setState({
        rowProps,
        selections,
        collapsed,
      });
    }
  }

  async componentDidUpdate(prevProps: Props) {
    if (this.props.activeHotel !== prevProps.activeHotel) {
      this._isMounted = true;
      const rowProps = await computeRowProps(this.props.activeHotel);

      if (this._isMounted) {
        const selections = { ...this.state.selections };
        const collapsed = [...this.state.collapsed];

        rowProps.rtConf.forEach((roomConfig: any) => {
          selections[roomConfig.id] = {
            selected: false,
            ratePlans: {},
          };
          roomConfig.ratePlanOptions.forEach((rp: any) => {
            selections[roomConfig.id].ratePlans[rp.id] = false;
          });
          collapsed.push(roomConfig.id);
        });

        this.setState({
          rowProps,
          selections,
          collapsed,
          allRTSelected: false,
          allRPSelected: false,
        });

        if (this.props.onSelection) {
          this.props.onSelection(selections);
        }
      }
    }

    // Handle clear trigger prop change with proper null checks
    if (
      prevProps.clearTrigger !== this.props.clearTrigger &&
      this.state.rowProps &&
      this.state.rowProps.rtConf &&
      Array.isArray(this.state.rowProps.rtConf)
    ) {
      const clearedSelections: State["selections"] = {};

      this.state.rowProps.rtConf.forEach((roomConfig: any) => {
        clearedSelections[roomConfig.id] = {
          selected: false,
          ratePlans: {},
        };
        if (
          roomConfig.ratePlanOptions &&
          Array.isArray(roomConfig.ratePlanOptions)
        ) {
          roomConfig.ratePlanOptions.forEach((rp: any) => {
            clearedSelections[roomConfig.id].ratePlans[rp.id] = false;
          });
        }
      });

      this.setState(
        {
          selections: clearedSelections,
          allRTSelected: false,
          allRPSelected: false,
        },
        () => {
          if (this.props.onSelection) {
            this.props.onSelection(clearedSelections);
          }
        },
      );
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  // Check if a rate plan is applicable to the currently selected periods
  private isRatePlanApplicable = (ratePlan: any): boolean => {
    // No period or no autoCloseOut info => show by default
    const { periods, weekDays } = this.props;
    const aco = ratePlan?.autoCloseOut;

    if (!periods || periods.length === 0) return true;
    if (!aco || !aco.openStart || !aco.openEnd) return true;

    const openStart = parseISO(String(aco.openStart));
    const openEnd = parseISO(String(aco.openEnd));
    const allDaysSelected =
      !weekDays || weekDays.length !== 7 ? true : weekDays.every(Boolean);

    // Overlaps any selected period?
    const overlapsAny = periods.some((p) => {
      const pStart = parseISO(String(p.start));
      const pEnd = parseISO(String(p.end));
      // inclusive overlap check
      const interStart = new Date(
        Math.max(pStart.getTime(), openStart.getTime()),
      );
      const interEnd = new Date(Math.min(pEnd.getTime(), openEnd.getTime()));
      if (interStart.getTime() > interEnd.getTime()) return false;
      if (allDaysSelected) return true;
      return this.intersectionHasSelectedWeekday(
        interStart,
        interEnd,
        weekDays!,
      );
    });

    return overlapsAny;
  };

  // Fast weekday hit-test in the intersection [start, end]
  private intersectionHasSelectedWeekday = (
    start: Date,
    end: Date,
    weekDays: boolean[],
  ): boolean => {
    const startJsDow = start.getDay();
    for (let monIdx = 0; monIdx < 7; monIdx++) {
      if (!weekDays[monIdx]) continue;
      const jsDow = (monIdx + 1) % 7;
      const offset = (jsDow - startJsDow + 7) % 7;
      const firstHit = new Date(start);
      firstHit.setDate(start.getDate() + offset);
      if (firstHit.getTime() <= end.getTime()) return true;
    }
    return false;
  };

  render() {
    const { hotelId, rate: baseRate, selectable } = this.props;
    const { selections, rowProps } = this.state;

    if (
      !baseRate ||
      !rowProps ||
      !(hotelId && baseRate && this.props.activeHotel)
    ) {
      return <div />;
    }

    const selectedMap: any = {};
    rowProps.rtConf.forEach((rt: any) => {
      let selected = 0;
      Object.keys(selections[rt.id].ratePlans).forEach((rp: string) => {
        // Count only currently visible/applicable rate plans
        const rpObj = rt.ratePlans.find((x: any) => x.id === rp);
        if (
          rpObj &&
          this.isRatePlanApplicable(rpObj) &&
          selections[rt.id].ratePlans[rpObj.id]
        ) {
          selected++;
        }
      });
      selectedMap[rt.id] = selected;
    });

    return (
      <ThemeProvider theme={baseTheme}>
        <Fragment>
          <br />
          {selectable && (
            <Fragment>
              <SectionTitle {...{ value: "Room and rateplans selection" }} />
              <Box sx={{ mt: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    mb: 2,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <Box sx={{ display: "inline-block" }}>
                    <TextField
                      id="input-with-icon-adornment"
                      placeholder="Search"
                      value={this.state.filterValue}
                      variant="outlined"
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search />
                          </InputAdornment>
                        ),
                        endAdornment: this.state.filterValue.length > 0 && (
                          <InputAdornment position="end">
                            <IconButton onClick={this.clearFilter} size="small">
                              <CancelRounded />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      onChange={this.applyFilter}
                      onClick={this.applyFilter}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "white",
                          borderRadius: 1,
                        },
                      }}
                    />
                  </Box>
                </Box>
                <ThemeProvider theme={tableTheme}>
                  <TableContainer component={Paper} sx={{ width: "80%" }}>
                    <Table size="small">
                      <colgroup>
                        <col style={{ width: "50%" }} />
                        <col style={{ width: "50%" }} />
                      </colgroup>
                      <TableBody>
                        {rowProps.rtConf.map(
                          ({
                            ratePlans,
                            id,
                            shortDescription,
                          }: {
                            ratePlans: any;
                            id: string;
                            shortDescription: string;
                          }) => (
                            <Fragment key={id}>
                              <TableRow>
                                <TableCell
                                  colSpan={2}
                                  sx={{
                                    fontWeight: "bold",
                                    py: 0.5,
                                    px: 1,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      position: "sticky",
                                      top: 0,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      {!this.props.manualOnly && (
                                        <Checkbox
                                          size="small"
                                          id={id}
                                          hidden={!selectable}
                                          checked={
                                            (selections[id] &&
                                              selections[id].selected) ||
                                            false
                                          }
                                          onChange={() =>
                                            this.toggle("roomType", id)
                                          }
                                          sx={{ p: 0, mr: 0.5 }}
                                        />
                                      )}
                                      <IconButton
                                        onClick={() => this.toggleCollapse(id)}
                                        size="small"
                                        sx={{ mr: 0.5, p: 0.25 }}
                                      >
                                        {this.state.collapsed.indexOf(id) !==
                                        -1 ? (
                                          <KeyboardArrowRight fontSize="small" />
                                        ) : (
                                          <KeyboardArrowDown fontSize="small" />
                                        )}
                                      </IconButton>
                                      <Typography
                                        variant="body2"
                                        component="span"
                                      >
                                        {shortDescription + " "}
                                        {selectedMap[id] > 0 ? (
                                          <Typography
                                            variant="caption"
                                            component="span"
                                            sx={{ fontWeight: "normal" }}
                                          >
                                            {" - " +
                                              selectedMap[id] +
                                              " selected "}
                                          </Typography>
                                        ) : (
                                          ""
                                        )}
                                      </Typography>
                                    </Box>
                                    {selectable && (
                                      <Button
                                        size="small"
                                        variant={
                                          this.areAllRatePlansSelected(id)
                                            ? "contained"
                                            : "outlined"
                                        }
                                        onClick={() =>
                                          this.toggleAllRatePlansForRoomType(id)
                                        }
                                        sx={{
                                          minWidth: "auto",
                                          fontSize: "0.75rem",
                                          padding: "2px 6px",
                                          height: "24px",
                                        }}
                                      >
                                        {this.areAllRatePlansSelected(id)
                                          ? "Unselect All"
                                          : "Select All"}
                                      </Button>
                                    )}
                                  </Box>
                                </TableCell>
                              </TableRow>
                              {ratePlans.map((ratePlan: any) => {
                                if (
                                  this.state.collapsed.indexOf(id) == -1 &&
                                  this.state.filtered.indexOf(ratePlan.id) ==
                                    -1 &&
                                  (ratePlan.isManual ||
                                    (!ratePlan.isManual &&
                                      !this.props.manualOnly)) &&
                                  this.isRatePlanApplicable(ratePlan)
                                ) {
                                  return (
                                    <TableRow key={`${id}_${ratePlan.id}`}>
                                      <TableCell
                                        sx={{
                                          borderBottom: "none",
                                          py: 0.25,
                                          px: 1,
                                        }}
                                      >
                                        <Box sx={{ ml: 1 }}>
                                          {" "}
                                          <FormControlLabel
                                            control={
                                              <Checkbox
                                                size="small"
                                                id={`${id}_${ratePlan.id}`}
                                                hidden={!selectable}
                                                disabled={
                                                  (selections[id] &&
                                                    selections[id].selected) ||
                                                  false
                                                }
                                                checked={
                                                  (selections[id] &&
                                                    selections[id].ratePlans &&
                                                    selections[id].ratePlans[
                                                      ratePlan.id
                                                    ]) ||
                                                  false
                                                }
                                                onChange={() =>
                                                  this.toggle(
                                                    "ratePlan",
                                                    id,
                                                    ratePlan.id,
                                                  )
                                                }
                                                sx={{ p: 0, mr: 0.5 }}
                                              />
                                            }
                                            label={
                                              <Typography variant="caption">
                                                {ratePlan.shortDescription}
                                                {ratePlan.isManual ? (
                                                  <Typography
                                                    variant="caption"
                                                    component="span"
                                                    sx={{
                                                      color: "error.main",
                                                    }}
                                                  >
                                                    {" "}
                                                    - (set rate)
                                                  </Typography>
                                                ) : (
                                                  ""
                                                )}
                                              </Typography>
                                            }
                                            sx={{ ml: 0, my: 0 }}
                                          />
                                        </Box>
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          borderBottom: "none",
                                          py: 0.25,
                                          px: 1,
                                        }}
                                      />
                                    </TableRow>
                                  );
                                }
                                return null;
                              })}
                            </Fragment>
                          ),
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </ThemeProvider>
              </Box>
            </Fragment>
          )}
        </Fragment>
      </ThemeProvider>
    );
  }

  toggleCollapse(id: string) {
    const co = this.state.collapsed;
    const index = co.indexOf(id);

    if (index === -1) {
      co.push(id);
      this.setState({ collapsed: co });
    } else {
      delete co[index];

      this.setState({ collapsed: co });
    }
  }

  collapseAll() {
    const c: string[] = [];
    this.state.rowProps?.rtConf.forEach((roomConfig: any) => {
      c.push(roomConfig.id);
    });
    this.setState({ collapsed: c });
  }

  toggleAllRT() {
    const { selections } = this.state;

    Object.keys(selections).forEach((rt) => {
      selections[rt].selected = !this.state.allRTSelected;
    });
    this.setState({ selections, allRTSelected: !this.state.allRTSelected });
    if (this.props.onSelection) {
      this.props.onSelection(selections);
    }
  }

  toggleAllRP() {
    const { selections } = this.state;

    this.state.rowProps?.rtConf.forEach((rt: any) => {
      if (!selections[rt.id].selected) {
        for (const rp of rt.ratePlans) {
          if (this.state.filtered.indexOf(rp.id) == -1) {
            const rps = selections[rt.id].ratePlans || {};
            rps[rp.id] = !this.state.allRPSelected;
            selections[rt.id].ratePlans = rps;
          }
        }
      }
    });
    this.setState({
      selections: selections,
      allRPSelected: !this.state.allRPSelected,
    });
    if (this.props.onSelection) {
      this.props.onSelection(selections);
    }
  }

  toggle(type: "roomType" | "ratePlan", roomType: string, ratePlan = "") {
    const { selections } = this.state;
    if (type === "roomType") {
      selections[roomType].selected = !selections[roomType].selected;
      if (selections[roomType].selected) {
        selections[roomType].ratePlans = {};
      }
    } else {
      selections[roomType].ratePlans[ratePlan] =
        !selections[roomType].ratePlans[ratePlan];
    }

    this.setState({ selections });
    if (this.props.onSelection) {
      this.props.onSelection(selections);
    }
  }

  toggleAllRatePlansForRoomType = (roomType: string) => {
    const { selections, rowProps } = this.state;
    const updatedSelections = { ...selections };

    if (rowProps && rowProps.rtConf) {
      const roomConfig = rowProps.rtConf.find(
        (config: any) => config.id === roomType,
      );
      if (roomConfig && roomConfig.ratePlans) {
        updatedSelections[roomType] = {
          selected: false,
          ratePlans: { ...updatedSelections[roomType].ratePlans },
        };

        // Get all visible rate plans
        const visibleRatePlans = roomConfig.ratePlans.filter(
          (rp: any) =>
            this.state.filtered.indexOf(rp.id) === -1 &&
            (rp.isManual || (!rp.isManual && !this.props.manualOnly)),
        );

        // Check if all visible rate plans are currently selected
        const allSelected = visibleRatePlans.every(
          (rp: any) => updatedSelections[roomType].ratePlans[rp.id] === true,
        );

        // If all are selected, unselect all. Otherwise, select all
        visibleRatePlans.forEach((rp: any) => {
          updatedSelections[roomType].ratePlans[rp.id] = !allSelected;
        });

        this.setState({ selections: updatedSelections });

        if (this.props.onSelection) {
          this.props.onSelection(updatedSelections);
        }

        console.log(
          `${allSelected ? "Unselected" : "Selected"} all rate plans for room type: ${roomType}`,
        );
      }
    }
  };

  areAllRatePlansSelected = (roomType: string): boolean => {
    const { selections, rowProps } = this.state;

    if (!rowProps || !rowProps.rtConf) return false;

    const roomConfig = rowProps.rtConf.find(
      (config: any) => config.id === roomType,
    );
    if (!roomConfig || !roomConfig.ratePlans) return false;

    // Get all visible rate plans
    const visibleRatePlans = roomConfig.ratePlans.filter(
      (rp: any) =>
        this.state.filtered.indexOf(rp.id) === -1 &&
        (rp.isManual || (!rp.isManual && !this.props.manualOnly)),
    );

    if (visibleRatePlans.length === 0) return false;

    // Check if all visible rate plans are selected
    return visibleRatePlans.every(
      (rp: any) => selections[roomType]?.ratePlans?.[rp.id] === true,
    );
  };

  // Add method to select all rate plans for a specific room type
  selectAllRatePlansForRoomType = (roomType: string) => {
    const { selections, rowProps } = this.state;
    const updatedSelections = { ...selections };

    if (rowProps && rowProps.rtConf) {
      const roomConfig = rowProps.rtConf.find(
        (config: any) => config.id === roomType,
      );
      if (roomConfig && roomConfig.ratePlans) {
        // Set room type as not selected (since we're selecting individual rate plans)
        updatedSelections[roomType] = {
          selected: false,
          ratePlans: { ...updatedSelections[roomType].ratePlans },
        };

        // Select all rate plans for this room type (only visible ones)
        roomConfig.ratePlans.forEach((rp: any) => {
          // Only select if not filtered out and matches manual/non-manual criteria
          if (
            this.state.filtered.indexOf(rp.id) === -1 &&
            (rp.isManual || (!rp.isManual && !this.props.manualOnly))
          ) {
            updatedSelections[roomType].ratePlans[rp.id] = true;
          }
        });

        this.setState({ selections: updatedSelections });

        if (this.props.onSelection) {
          this.props.onSelection(updatedSelections);
        }

        console.log(`Selected all rate plans for room type: ${roomType}`);
      }
    }
  };

  clearAllSelections = () => {
    const { selections } = this.state;
    const clearedSelections = { ...selections };

    // Clear all room type selections and rate plan selections
    Object.keys(clearedSelections).forEach((roomType) => {
      clearedSelections[roomType] = {
        selected: false,
        ratePlans: Object.keys(
          clearedSelections[roomType].ratePlans || {},
        ).reduce((acc, ratePlan) => ({ ...acc, [ratePlan]: false }), {}),
      };
    });

    this.setState({
      selections: clearedSelections,
      allRTSelected: false,
      allRPSelected: false,
    });

    // Notify parent of the cleared selections
    if (this.props.onSelection) {
      this.props.onSelection(clearedSelections);
    }

    console.log("BulkUpdatePreview: All selections cleared");
  };

  clearFilter(event: any) {
    this.setState({ filtered: [], filterValue: "" });
    event.stopPropagation();
    return;
  }

  applyFilter(event: any) {
    if (!event.target.value) {
      this.setState({ filterValue: event.target.value });
      return;
    }
    const v = event.target.value.toLowerCase();
    if (v.length < 2) {
      this.setState({ filtered: [], filterValue: event.target.value });
      event.stopPropagation();
      return;
    }
    const filtered: string[] = [];
    this.state.rowProps?.rtConf.forEach((roomConfig: any) => {
      roomConfig.ratePlans.forEach((rp: any) => {
        const tokens = v.split(" ");
        for (const token of tokens) {
          if (
            !rp.shortDescription
              .replace(/[^\w\s]/gi, "")
              .toLowerCase()
              .includes(token.replace(/[^\w\s]/gi, ""))
          ) {
            filtered.push(rp.id);
          }
        }
      });
    });

    this.setState({
      filtered: filtered,
      collapsed: [],
      filterValue: event.target.value,
    });
  }
}

const mapStateToProps = (state: any) => ({
  activeHotel: state.hotelData.activeHotel,
});

const BulkUpdatePreviewObserver = connect(mapStateToProps)(BulkUpdatePreview);
export { BulkUpdatePreviewObserver as BulkUpdatePreview };
